import { getEngagementFeatureCommentsPage } from '@services/youtube-platform.service';

export type EngageFeatureType = 'talkingTiles' | 'transientThought';

export interface EngagementComment {
  words?: string;
  body?: string;
  username?: string;
  [key: string]: any;
}

export interface EngagementCommentsPage {
  comments: EngagementComment[];
  total: number;
}

interface EngagementCommentsResponse {
  status: boolean;
  entity: { comments: EngagementComment[]; total: number } | null;
}

/** `getWithAuth` sets no axios timeout, so a hung GET never settles. */
const REQUEST_DEADLINE_MS = 30_000;

const fetchPage = getEngagementFeatureCommentsPage as (
  engageType: EngageFeatureType,
  id: string,
  offset: number,
) => Promise<EngagementCommentsResponse>;

export class EngagementCommentsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EngagementCommentsError';
  }
}

const withDeadline = <T,>(promise: Promise<T>, ms: number): Promise<T> => new Promise<T>((resolve, reject) => {
  const timer = setTimeout(
    () => reject(new EngagementCommentsError('Engagement comments request timed out')),
    ms,
  );
  promise.then(resolve, reject).finally(() => clearTimeout(timer));
});

/**
 * Fetches one older page of processed comments (newest-first offset, page
 * contents returned oldest-to-newest). Used to page backwards through
 * comments once an interaction is paused/stopped.
 */
export const fetchCommentsPage = async (
  engageType: EngageFeatureType,
  featureId: string,
  offset: number,
): Promise<EngagementCommentsPage> => {
  const response = await withDeadline(
    fetchPage(engageType, featureId, offset),
    REQUEST_DEADLINE_MS,
  );

  if (!response?.status) {
    throw new EngagementCommentsError(`Failed to fetch ${engageType} comments`);
  }

  return {
    comments: response.entity?.comments ?? [],
    total: response.entity?.total ?? 0,
  };
};
